import { IconArrowRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { InstanceActivityItemModel } from '../../../../../apis/v1/instances/model';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { activityActor } from '@xver/utils/projects';
import { useInstanceActivityQuery } from '../hooks/useInstanceActivityQuery';
import { getActivityMeta } from './activityTypeMeta';

interface ActivityCardProps {
    instanceId: number;
    onViewAll?: () => void;
}

function formatActivityTime(iso: string): string {
    const d = dayjs(iso);
    if (!d.isValid()) return '-';
    if (dayjs().diff(d, 'day') < 7) return d.fromNow();
    return d.format('MMM D, YYYY');
}

function ViewAllLink({ onClick }: { onClick?: () => void }) {
    const { t } = useTranslation('cloudComputeInstance');
    return (
        <button
            type="button"
            onClick={onClick}
            className="text-sm text-blue-400 hover:text-blue-300 light:text-blue-700 light:hover:text-blue-800 inline-flex items-center gap-1 transition-colors"
        >
            <span>{t('activity.viewAll')}</span>
            <IconArrowRight size={14} />
        </button>
    );
}

function ActivityRow({ item }: { item: InstanceActivityItemModel }) {
    const { t } = useTranslation('cloudComputeInstance');
    const { Icon, color } = getActivityMeta(item.type);
    const actor = activityActor(item, t('activity.systemActor'), t('activity.someoneActor'));
    const timeLabel = formatActivityTime(item.created_at);

    return (
        <div className="w-full flex flex-row items-start gap-3 px-4 py-3 border-t-[0.5px] border-line first:border-t-0">
            <div
                className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${color}26` }}
            >
                <Icon size={16} color={color} />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
                <div className="text-[15px] text-fg wrap-break-word">{item.description}</div>
                <div className="text-xs text-fg/68 mt-0.5">
                    {actor} · {timeLabel}
                </div>
            </div>
        </div>
    );
}

function SkeletonRow() {
    return (
        <div className="w-full flex flex-row items-start gap-3 px-4 py-3 border-t-[0.5px] border-line first:border-t-0">
            <div className="w-8 h-8 rounded-md bg-surface-3 animate-pulse shrink-0" />
            <div className="flex flex-col min-w-0 flex-1 gap-1.5">
                <div className="h-3 w-3/4 rounded bg-surface-3 animate-pulse" />
                <div className="h-2.5 w-1/3 rounded bg-surface-3 animate-pulse" />
            </div>
        </div>
    );
}

export default function ActivityCard({ instanceId, onViewAll }: ActivityCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const { data, isPending } = useInstanceActivityQuery(instanceId, 10);
    const items = data?.data ?? [];
    const loading = isPending && !data;

    return (
        <DialogBox
            label={t('activity.recentTitle')}
            rightDescription={<ViewAllLink onClick={onViewAll} />}
            classNames=""
        >
            {loading ? (
                <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                </>
            ) : items.length === 0 ? (
                <div className="w-full p-6 text-sm text-fg/68 text-center">
                    {t('activity.noRecentActivity')}
                </div>
            ) : (
                items.map((item) => <ActivityRow key={item.id} item={item} />)
            )}
        </DialogBox>
    );
}
